<script setup lang="ts">
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import Textarea from 'primevue/textarea';
import Button from 'primevue/button';
import Message from 'primevue/message';
import ColorPickerField from 'shared/forms/components/ColorPickerField.vue';
import { useProductGroupStore } from 'shared/products/groups/stores/productGroupStore';
import { useCreateProductGroup, type CreateProductGroupRequest } from '../api/createProductGroup';
import { useUpdateProductGroup, type UpdateProductGroupRequest } from '../api/updateProductGroup';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import ProblemDetailsView from 'shared/problems/components/ProblemDetailsView.vue';
import { useRefereshProductGroupStore } from '../commands/refreshProductGroupStore';
import { ProductType } from 'shared/products/domain/productType';
import type { ProductGroup } from 'shared/products/groups/domain/productGroup';
import { Assert } from 'shared/assert';
import type { ProblemDetails } from "shared/problems/domain/problemDetails";

defineExpose({
  show
});

const store = useProductGroupStore();
const createGroup = useCreateProductGroup();
const updateGroup = useUpdateProductGroup();
const languageStore = useLanguageStore();
const refreshGroupStore = useRefereshProductGroupStore();
const { translations } = storeToRefs(languageStore);

const loading = ref(false);
const problems = ref<ProblemDetails>();

const types = [
  ProductType.Undefined,
  ProductType.Log,
  ProductType.Coil,
  ProductType.Bulk
];

const typeOptions = types.map<TypeOption>((t) => {
  return {
    value: t,
    name: translations.value.productType(t)
  };
});

const defaultType = typeOptions.find(o => o.value === ProductType.Log) ?? typeOptions[0]!;

const operation = ref<'create' | 'edit' | 'none'>('none');
const visible = ref<boolean>();

const current = ref<ProductGroup>();

const title = ref('');

const name = ref('');
const nameChanged = computed(() => {
  if (operation.value !== 'edit') return false;
  const group = current.value;
  if (!group) return false;
  return name.value !== group.name;
});
const nameError = computed(() => {

  if (name.value === '') {
    return translations.value.error.required;
  }

  if (name.value.length > 100) {
    return translations.value.error.maxLength(100);
  }

  return undefined;
});

const selectedType = ref<TypeOption>(defaultType);
const selectedTypeChanged = computed(() => {
  if (operation.value !== 'edit') return false;
  const group = current.value;
  if (!group) return false;
  return selectedType.value.value !== group.productType;
});

const color = ref('#3b82f6');
const colorChanged = computed(() => {
  if (operation.value !== 'edit') return false;
  const group = current.value;
  if (!group) return false;
  return color.value !== group.color;
});

const description = ref('');
const descriptionChanged = computed(() => {
  if (operation.value !== 'edit') return false;
  const group = current.value;
  if (!group) return false;
  return description.value !== group.description;
});

const valid = computed(() => {
  if (nameError.value !== undefined) return false;
  return true;
});

const hasChanges = computed(() => {
  return nameChanged.value ||
    colorChanged.value ||
    descriptionChanged.value ||
    selectedTypeChanged.value;
});

const saveEnabled = computed(() => {
  return valid.value && (operation.value === 'create' || (operation.value === 'edit' && hasChanges))
});

interface TypeOption {
  name: string;
  value: ProductType;
}

interface ShowDialog {
  groupId?: string;
}

async function show(options: ShowDialog) {
  try {
    loading.value = true;
    problems.value = undefined;

    const id = options.groupId;

    if (id === undefined) {
      showCreate();
    } else {
      await showEdit(id);
    }

  } finally {
    loading.value = false;
  }
}

function showCreate() {
  title.value = translations.value.productGroups.listPage.addNew;
  name.value = '';
  description.value = '';
  color.value = '3b82f6';
  selectedType.value = defaultType;

  current.value = undefined;
  operation.value = 'create';
  visible.value = true;
}

async function showEdit(id: string) {
  title.value = translations.value.productGroups.listPage.edit;
  await refreshGroupStore.initialize();
  const group = store.getById(id);
  Assert.definied(group);

  name.value = group.name;
  color.value = group.color ?? '3b82f6';
  description.value = group.description;
  selectedType.value = typeOptions.find(o => o.value === group.productType) ?? defaultType;

  operation.value = 'edit';
  current.value = group;
  visible.value = true;
}

async function onSubmit() {
  if (!valid.value) return;

  try {
    loading.value = true;

    if (operation.value === 'create') {
      await create();
    } else {
      const id = current.value?.id;
      Assert.definied(id);
      await update(id);
    }

  } finally {
    loading.value = false;
  }
}

async function create() {
  const request: CreateProductGroupRequest = {
    group: {
      name: name.value,
      description: description.value,
      productType: selectedType.value.value,
      color: color.value
    }
  };

  const response = await createGroup.execute(request);

  if (!response.ok) {
    problems.value = response.details;
    return;
  }

  operation.value = 'none';
  visible.value = false;
}

async function update(id: string) {

  const request: UpdateProductGroupRequest = {
    groupId: id,
    group: {}
  };

  if (nameChanged.value) {
    request.group.name = name.value;
  }

  if (descriptionChanged.value) {
    request.group.description = description.value;
  }

  if (color.value) {
    request.group.color = color.value;
  }

  const response = await updateGroup.execute(request);

  if (!response.ok) {
    problems.value = response.details;
    return;
  }

  current.value = undefined;
  operation.value = 'none';
  visible.value = false;
}

function onHide() {
  current.value = undefined;
  operation.value = 'none';
  visible.value = false;
}

</script>

<template>
  <Dialog modal v-model:visible="visible" :header="title" @hide="onHide">
    <ProblemDetailsView v-model="problems"></ProblemDetailsView>
    <div class="product-group-form">
      <div class="field" :class="{ changed: nameChanged }">
        <label for="edit-product-group-dialog-name">{{ translations.generic.name }}</label>
        <InputText autocomplete="off" v-model:modelValue="name" autofocus id="edit-product-group-dialog-name"
          name="name"></InputText>
        <Message v-if="nameError" severity="error" variant="simple">{{ nameError }}</Message>
      </div>
      <div class="field" :class="{ changed: selectedTypeChanged }">
        <label for="edit-product-group-dialog-type">{{ translations.generic.type }}</label>
        <Select autocomplete="off" :disabled="operation !== 'create'" id="edit-product-group-dialog-type" name="type"
          :options="typeOptions" optionLabel="name" v-model="selectedType"></Select>
      </div>
      <div class="field description-field" :class="{ changed: descriptionChanged }">
        <label for="edit-product-group-dialog-description">{{ translations.generic.description }}</label>
        <Textarea autocomplete="off" v-model:modelValue="description" id="edit-product-group-dialog-description"
          name="description"></Textarea>
      </div>
      <ColorPickerField :changed="colorChanged" v-model:model-value="color"></ColorPickerField>
      <Button @click="onSubmit" :disabled="!saveEnabled" class="submit-button" :label="translations.generic.save"
        type="submit" :loading="loading"></Button>
    </div>
  </Dialog>
</template>

<style scoped>
.product-group-form {
  display: grid;
  row-gap: .5rem;
  column-gap: .5rem;
  grid-template-columns: repeat(3, 1fr);
}

.description-field {
  grid-column: 1/4;
}

.field {
  display: flex;
  flex-direction: column;
}

.submit-button {
  justify-self: end;
  grid-column: 1/4;
}
</style>
